import { dirname } from 'node:path';
import type { FC } from 'react';
import React from 'react';
import { Box, Learn, Link, Text } from '@wix/cli-ui-kit';
import type { ComponentGenerationResult } from '@wix/gena';
import { useProjectModel } from '../../model.js';

interface GenerationSuccessMessageProps {
  extensionType: string;
  generatedFiles: ComponentGenerationResult['newFiles'];
  learnMoreUrl: string;
  children?: React.ReactNode;
}

export const GenerationSuccessMessage: FC<GenerationSuccessMessageProps> = ({
  extensionType,
  generatedFiles,
  learnMoreUrl,
  children,
}) => {
  const folders = generatedFiles.reduce<Record<string, string[]>>(
    (acc, file) => {
      const dir = dirname(file);

      acc[dir] = [...(acc[dir] ?? []), file];
      return acc;
    },
    {}
  );

  return (
    <Box
      borderColor="blue"
      borderStyle="round"
      flexDirection="column"
      paddingY={1}
      paddingX={3}
      gap={1}
      marginTop={1}
    >
      <Box marginTop={-2} marginLeft={0}>
        <Text skin="question" bold>
          Generated Successfully
        </Text>
      </Box>
      <Text>🚀 The new {extensionType} has been generated successfully.</Text>
      {children}
      {Object.entries(folders).map(([folder, files]) => (
        <Folder key={folder} folderName={folder} files={files} />
      ))}
      <Text>
        <Learn /> Learn more about Wix headless: <Link url={learnMoreUrl} />
      </Text>
    </Box>
  );
};

const Folder: FC<{ folderName: string; files: string[] }> = ({
  folderName,
  files,
}) => {
  const { model } = useProjectModel();

  const relativeFilePath = (absolutePath: string) =>
    absolutePath.replace(model.projectFolder, '.');

  return (
    <Box flexDirection="column">
      <Text>📁 {folderName}</Text>
      <Box flexDirection="column" marginLeft={3} marginTop={1}>
        {files.map((file) => (
          <Text key={file}>{relativeFilePath(file)}</Text>
        ))}
      </Box>
    </Box>
  );
};
